import { Group } from '@app/shared/models/group';
import { Role } from '@app/shared/models/role';
import { User } from '@app/shared/models/user';
import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { GroupsActions } from '../actions';


export const groupsFeatureKey = 'groups';

export interface State {
  groups: Group[];
  groupsLoading: boolean;
  groupsLoaded: boolean;
  groupsError: any;

  groupDetails: Group;
  groupDetailsLoading: boolean;
  groupDetailsLoaded: boolean;
  groupDetailsError: any;

  groupRoles: Role[];
  groupRolesLoading: boolean;
  groupRolesLoaded: boolean;
  groupRolesError: any;

  teamRoles: Role[];
  teamRolesLoading: boolean;
  teamRolesLoaded: boolean;
  teamRolesError: any;

  groupMembers: User[];
  groupMembersLoading: boolean;
  groupMembersLoaded: boolean;
  groupMembersError: any;

  accountMembers: User[];
  accountMembersLoading: boolean;
  accountMembersLoaded: boolean;
  accountMembersError: any;

  createGroupPending: boolean;
  createGroupSuccess: boolean;
  createGroupError: any;

  attachRoleToGroupPending: boolean;
  attachRoleToGroupSuccess: boolean;
  attachRoleToGroupError: any;

  detachRoleFromGroupPending: boolean;
  detachRoleFromGroupSuccess: boolean;
  detachRoleFromGroupError: any;

  attachMemberToGroupPending: boolean;
  attachMemberToGroupSuccess: boolean;
  attachMemberToGroupError: any;

  detachMemberFromGroupPending: boolean;
  detachMemberFromGroupSuccess: boolean;
  detachMemberFromGroupError: any;

  groupUpdating: boolean;
  groupUpdated: boolean;
  groupUpdatingError: any;

  deleteGroupPending: boolean;
  deleteGroupSuccess: boolean;
  deleteGroupError: any;
}

export const initialState: State = {
  groups: null,
  groupsLoading: false,
  groupsLoaded: false,
  groupsError: null,

  groupDetails: null,
  groupDetailsLoading: false,
  groupDetailsLoaded: false,
  groupDetailsError: null,

  groupRoles: [],
  groupRolesLoading: false,
  groupRolesLoaded: false,
  groupRolesError: null,

  teamRoles: [],
  teamRolesLoading: false,
  teamRolesLoaded: false,
  teamRolesError: null,

  groupMembers: [],
  groupMembersLoading: false,
  groupMembersLoaded: false,
  groupMembersError: null,

  accountMembers: [],
  accountMembersLoading: false,
  accountMembersLoaded: false,
  accountMembersError: null,

  createGroupPending: false,
  createGroupSuccess: false,
  createGroupError: null,

  attachRoleToGroupPending: false,
  attachRoleToGroupSuccess: false,
  attachRoleToGroupError: null,

  detachRoleFromGroupPending: false,
  detachRoleFromGroupSuccess: false,
  detachRoleFromGroupError: null,

  attachMemberToGroupPending: false,
  attachMemberToGroupSuccess: false,
  attachMemberToGroupError: null,

  detachMemberFromGroupPending: false,
  detachMemberFromGroupSuccess: false,
  detachMemberFromGroupError: null,

  groupUpdating: false,
  groupUpdated: false,
  groupUpdatingError: null,

  deleteGroupPending: false,
  deleteGroupSuccess: false,
  deleteGroupError: null,
};

const groupsReducer = createReducer(
  initialState,
  // Load groups
  on(GroupsActions.LoadGroups, (state) => ({
    ...state,
    groups: initialState.groups,
    groupsLoading: true,
    groupsLoaded: initialState.groupsLoaded,
    groupsError: initialState.groupsError
  })),
  on(GroupsActions.LoadGroupsSuccess, (state, { groups }) => ({
    ...state,
    groups,
    groupsLoading: false,
    groupsLoaded: true,
    groupsError: null
  })),
  on(GroupsActions.LoadGroupsFailure, (state, { error }) => ({
    ...state,
    groupsLoading: false,
    groupsError: error
  })),
  // Load group details
  on(GroupsActions.LoadGroupDetails, (state) => ({
    ...state,
    groupDetails: initialState.groupDetails,
    groupDetailsLoading: false,
    groupDetailsLoaded: initialState.groupDetailsLoaded,
    groupDetailsError: initialState.groupDetailsError
  })),
  on(GroupsActions.LoadGroupDetailsSuccess, (state, { group }) => ({
    ...state,
    groupDetails: group,
    groupDetailsLoading: false,
    groupDetailsLoaded: true,
    groupDetailsError: null
  })),
  on(GroupsActions.LoadGroupDetailsFailure, (state, { error }) => ({
    ...state,
    groupDetailsLoading: false,
    groupDetailsError: error
  })),
  // Load group roles
  on(GroupsActions.LoadGroupRoles, (state) => ({
    ...state,
    groupRoles: initialState.groupRoles,
    groupRolesLoading: true,
    groupRolesLoaded: initialState.groupRolesLoaded,
    groupRolesError: initialState.groupRolesError
  })),
  on(GroupsActions.LoadGroupRolesSuccess, (state, { roles }) => ({
    ...state,
    groupRoles: roles,
    groupRolesLoading: false,
    groupRolesLoaded: true,
    groupRolesError: null
  })),
  on(GroupsActions.LoadGroupRolesFailure, (state, { error }) => ({
    ...state,
    groupRolesLoading: false,
    groupRolesError: error
  })),
  // Load Team roles
  on(GroupsActions.LoadAttributableRoles, (state) => ({
    ...state,
    teamRoles: initialState.teamRoles,
    teamRolesLoading: true,
    teamRolesLoaded: initialState.teamRolesLoaded,
    teamRolesError: initialState.teamRolesError
  })),
  on(GroupsActions.LoadAttributableRolesSuccess, (state, { roles }) => ({
    ...state,
    teamRoles: roles,
    teamRolesLoading: false,
    teamRolesLoaded: true,
    teamRolesError: null
  })),
  on(GroupsActions.LoadAttributableRolesFailure, (state, { error }) => ({
    ...state,
    teamRolesLoading: false,
    teamRolesError: error
  })),
  // Load group members
  on(GroupsActions.LoadGroupMembers, (state) => ({
    ...state,
    groupMembers: initialState.groupMembers,
    groupMembersLoading: true,
    groupMembersLoaded: initialState.groupMembersLoaded,
    groupMembersError: initialState.groupMembersError
  })),
  on(GroupsActions.LoadGroupMembersSuccess, (state, { members }) => ({
    ...state,
    groupMembers: members,
    groupMembersLoading: false,
    groupMembersLoaded: true,
    groupMembersError: null
  })),
  on(GroupsActions.LoadGroupMembersFailure, (state, { error }) => ({
    ...state,
    groupMembersLoading: false,
    groupMembersError: error
  })),
  // Load account members
  on(GroupsActions.LoadAccountMembers, (state) => ({
    ...state,
    accountMembers: initialState.accountMembers,
    accountMembersLoading: true,
    accountMembersLoaded: initialState.accountMembersLoaded,
    accountMembersError: initialState.accountMembersError
  })),
  on(GroupsActions.LoadAccountMembersSuccess, (state, { members }) => ({
    ...state,
    accountMembers: members,
    accountMembersLoading: false,
    accountMembersLoaded: true,
    accountMembersError: null
  })),
  on(GroupsActions.LoadAccountMembersFailure, (state, { error }) => ({
    ...state,
    accountMembersLoading: false,
    accountMembersError: error
  })),
  // Create group
  on(GroupsActions.CreateGroup, (state) => ({
    ...state,
    createGroupPending: true,
    createGroupSuccess: initialState.createGroupSuccess,
    createGroupError: initialState.createGroupError
  })),
  on(GroupsActions.CreateGroupSuccess, (state) => ({
    ...state,
    createGroupPending: false,
    createGroupSuccess: true,
    createGroupError: null
  })),
  on(GroupsActions.CreateGroupFailure, (state, { error }) => ({
    ...state,
    createGroupPending: false,
    createGroupError: error
  })),
  // Attach role to group
  on(GroupsActions.AttachRoleToGroup, (state) => ({
    ...state,
    attachRoleToGroupPending: true,
    attachRoleToGroupSuccess: initialState.attachRoleToGroupSuccess,
    attachRoleToGroupError: initialState.attachRoleToGroupError
  })),
  on(GroupsActions.AttachRoleToGroupSuccess, (state) => ({
    ...state,
    attachRoleToGroupPending: false,
    attachRoleToGroupSuccess: true,
    attachRoleToGroupError: null
  })),
  on(GroupsActions.AttachRoleToGroupFailure, (state, { error }) => ({
    ...state,
    attachRoleToGroupPending: false,
    attachRoleToGroupError: error
  })),
  // Detach role from group
  on(GroupsActions.DetachRoleFromGroup, (state) => ({
    ...state,
    detachRoleFromGroupPending: true,
    detachRoleFromGroupSuccess: initialState.detachRoleFromGroupSuccess,
    detachRoleFromGroupError: initialState.detachRoleFromGroupError
  })),
  on(GroupsActions.DetachRoleFromGroupSuccess, (state) => ({
    ...state,
    detachRoleFromGroupPending: false,
    detachRoleFromGroupSuccess: true,
    detachRoleFromGroupError: null
  })),
  on(GroupsActions.DetachRoleFromGroupFailure, (state, { error }) => ({
    ...state,
    detachRoleFromGroupPending: false,
    detachRoleFromGroupError: error
  })),
  // Attach member to group
  on(GroupsActions.AttachMemberToGroup, (state) => ({
    ...state,
    attachMemberToGroupPending: true,
    attachMemberToGroupSuccess: initialState.attachMemberToGroupSuccess,
    attachMemberToGroupError: initialState.attachMemberToGroupError
  })),
  on(GroupsActions.AttachMemberToGroupSuccess, (state) => ({
    ...state,
    attachMemberToGroupPending: false,
    attachMemberToGroupSuccess: true,
    attachMemberToGroupError: null
  })),
  on(GroupsActions.AttachMemberToGroupFailure, (state, { error }) => ({
    ...state,
    attachMemberToGroupPending: false,
    attachMemberToGroupError: error
  })),
  // Detach member from group
  on(GroupsActions.DetachMemberFromGroup, (state) => ({
    ...state,
    detachMemberFromGroupPending: true,
    detachMemberFromGroupSuccess: initialState.detachMemberFromGroupSuccess,
    detachMemberFromGroupError: initialState.detachMemberFromGroupError
  })),
  on(GroupsActions.DetachMemberFromGroupSuccess, (state) => ({
    ...state,
    detachMemberFromGroupPending: false,
    detachMemberFromGroupSuccess: true,
    detachMemberFromGroupError: null
  })),
  on(GroupsActions.DetachMemberFromGroupFailure, (state, { error }) => ({
    ...state,
    detachMemberFromGroupPending: false,
    detachMemberFromGroupError: error
  })),
  // Update Team
  on(GroupsActions.UpdateGroup, (state) => ({
    ...state,
    groupUpdating: true,
    groupUpdated: initialState.groupUpdated,
    groupUpdatingError: initialState.groupUpdatingError,
  })),
  on(GroupsActions.UpdateGroupSuccess, (state, { group }) => ({
    ...state,
    groupUpdating: initialState.groupUpdating,
    groupUpdated: true,
  })),
  on(GroupsActions.UpdateGroupFailure, (state, { error }) => ({
    ...state,
    groupUpdating: initialState.groupUpdating,
    groupUpdatingError: error
  })),
  // Delete Group
  on(GroupsActions.DeleteGroup, (state) => ({
    ...state,
    deleteGroupPending: true,
    deleteGroupSuccess: initialState.deleteGroupSuccess,
    deleteGroupError: initialState.deleteGroupError
  })),
  on(GroupsActions.DeleteGroupSuccess, (state) => ({
    ...state,
    deleteGroupPending: false,
    deleteGroupSuccess: true,
    deleteGroupError: null
  })),
  on(GroupsActions.DeleteGroupFailure, (state, { error }) => ({
    ...state,
    deleteGroupPending: false,
    deleteGroupError: error
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return groupsReducer(state, action);
}

export const getGroupsState = createFeatureSelector<State>('groups');

export const getGroups = createSelector(getGroupsState, (state) => state.groups);
export const getGroupsLoading = createSelector(getGroupsState, (state) => state.groupsLoading);
export const getGroupsLoaded = createSelector(getGroupsState, (state) => state.groupsLoaded);
export const getGroupsError = createSelector(getGroupsState, (state) => state.groupsError);

export const getGroupDetails = createSelector(getGroupsState, (state) => state.groupDetails);
export const getGroupDetailsLoading = createSelector(getGroupsState, (state) => state.groupDetailsLoading);
export const getGroupDetailsLoaded = createSelector(getGroupsState, (state) => state.groupDetailsLoaded);
export const getGroupDetailsError = createSelector(getGroupsState, (state) => state.groupDetailsError);

export const getTeamRoles = createSelector(getGroupsState, (state) => state.teamRoles);
export const getTeamRolesLoading = createSelector(getGroupsState, (state) => state.teamRolesLoading);
export const getTeamRolesLoaded = createSelector(getGroupsState, (state) => state.teamRolesLoaded);
export const getTeamRolesError = createSelector(getGroupsState, (state) => state.teamRolesError);

export const getGroupRoles = createSelector(getGroupsState, (state) => state.groupRoles);
export const getGroupRolesLoading = createSelector(getGroupsState, (state) => state.groupRolesLoading);
export const getGroupRolesLoaded = createSelector(getGroupsState, (state) => state.groupRolesLoaded);
export const getGroupRolesError = createSelector(getGroupsState, (state) => state.groupRolesError);

export const getGroupMembers = createSelector(getGroupsState, (state) => state.groupMembers);
export const getGroupMembersLoading = createSelector(getGroupsState, (state) => state.groupMembersLoading);
export const getGroupMembersLoaded = createSelector(getGroupsState, (state) => state.groupMembersLoaded);
export const getGroupMembersError = createSelector(getGroupsState, (state) => state.groupMembersError);

export const getAccountMembers = createSelector(getGroupsState, (state) => state.accountMembers);
export const getAccountMembersLoading = createSelector(getGroupsState, (state) => state.accountMembersLoading);
export const getAccountMembersLoaded = createSelector(getGroupsState, (state) => state.accountMembersLoaded);
export const getAccountMembersError = createSelector(getGroupsState, (state) => state.accountMembersError);

export const getCreateGroupPending = createSelector(getGroupsState, (state) => state.createGroupPending);
export const getCreateGroupSuccess = createSelector(getGroupsState, (state) => state.createGroupSuccess);
export const getCreateGroupError = createSelector(getGroupsState, (state) => state.createGroupError);

export const getAttachRoleToGroupPending = createSelector(getGroupsState, (state) => state.attachRoleToGroupPending);
export const getAttachRoleToGroupSuccess = createSelector(getGroupsState, (state) => state.attachRoleToGroupSuccess);
export const getAttachRoleToGroupError = createSelector(getGroupsState, (state) => state.attachRoleToGroupError);

export const getDetachRoleFromGroupPending = createSelector(getGroupsState, (state) => state.detachRoleFromGroupPending);
export const getDetachRoleFromGroupSuccess = createSelector(getGroupsState, (state) => state.detachRoleFromGroupSuccess);
export const getDetachRoleFromGroupError = createSelector(getGroupsState, (state) => state.detachRoleFromGroupError);

export const getAttachMemberToGroupPending = createSelector(getGroupsState, (state) => state.attachMemberToGroupPending);
export const getAttachMemberToGroupSuccess = createSelector(getGroupsState, (state) => state.attachMemberToGroupSuccess);
export const getAttachMemberToGroupError = createSelector(getGroupsState, (state) => state.attachMemberToGroupError);

export const getDetachMemberFromGroupPending = createSelector(getGroupsState, (state) => state.detachMemberFromGroupPending);
export const getDetachMemberFromGroupSuccess = createSelector(getGroupsState, (state) => state.detachMemberFromGroupSuccess);
export const getDetachMemberFromGroupError = createSelector(getGroupsState, (state) => state.detachMemberFromGroupError);

export const getGroupUpdating = createSelector(getGroupsState, (state) => state.groupUpdating);
export const getGroupUpdated = createSelector(getGroupsState, (state) => state.groupUpdated);
export const getGroupUpdatingError = createSelector(getGroupsState, (state) => state.groupUpdatingError);

export const getDeleteGroupPending = createSelector(getGroupsState, (state) => state.deleteGroupPending);
export const getDeleteGroupSuccess = createSelector(getGroupsState, (state) => state.deleteGroupSuccess);
export const getDeleteGroupError = createSelector(getGroupsState, (state) => state.deleteGroupError);